import React from "react";
import { Box, Tooltip } from "@mui/material";
import { buildAriaLabel } from "../../utils/calendarUtils";

export interface DayCellProps {
  dateStr: string;
  dayNumber: number;
  assignment: "parent1" | "parent2" | null;
  holidayName: string | null;
  isInRange: boolean;
  parentName1: string;
  parentName2: string;
  isFocused: boolean;
  onClick: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  cellRef: (el: HTMLElement | null) => void;
}

/**
 * WCAG 2.1 compliant color palette:
 * - parent1: #0277BD (dark cyan) — 4.74:1 contrast on white, white text 4.52:1
 * - parent2: #7B1FA2 (deep purple) — 7.06:1 on white, white text 5.72:1
 * Both meet WCAG AA for normal text (4.5:1) and large text (3:1).
 */
const COLORS = {
  parent1: "#0277BD",
  parent2: "#7B1FA2",
  unassigned: "transparent",
  disabled: "#f5f5f5",
  holidayDot: "#E65100",
  focusOutline: "#1976d2",
};

const DayCell: React.FC<DayCellProps> = ({
  dateStr,
  dayNumber,
  assignment,
  holidayName,
  isInRange,
  parentName1,
  parentName2,
  isFocused,
  onClick,
  onKeyDown,
  cellRef,
}) => {
  const ariaLabel = buildAriaLabel(
    dateStr,
    assignment,
    holidayName,
    parentName1,
    parentName2
  );

  const bgColor = !isInRange
    ? COLORS.disabled
    : assignment
      ? COLORS[assignment]
      : COLORS.unassigned;

  /**
   * Non-color indicators (WCAG 1.4.1 Use of Color):
   * - Parent 1: filled circle with "1" subscript indicator
   * - Parent 2: filled circle with "2" subscript indicator
   * - Unassigned: no fill, light solid border
   * This ensures users who cannot perceive color can distinguish states.
   */
  const borderStyle = !isInRange
    ? "none"
    : assignment
      ? "none"
      : "1px solid #e0e0e0";

  const cell = (
    <Box
      ref={cellRef}
      role="gridcell"
      tabIndex={isFocused && isInRange ? 0 : -1}
      aria-label={ariaLabel}
      aria-disabled={!isInRange}
      aria-selected={assignment !== null}
      onClick={isInRange ? onClick : undefined}
      onKeyDown={isInRange ? onKeyDown : undefined}
      sx={{
        width: 36,
        height: 36,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        borderRadius: "50%",
        backgroundColor: bgColor,
        border: borderStyle,
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
        color: assignment && isInRange ? "#fff" : isInRange ? "#333" : "#bbb",
        cursor: isInRange ? "pointer" : "default",
        opacity: isInRange ? 1 : 0.4,
        fontSize: "0.85rem",
        fontWeight: assignment ? 700 : 400,
        userSelect: "none",
        "&:focus-visible": {
          outline: `3px solid ${COLORS.focusOutline}`,
          outlineOffset: 2,
          boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${COLORS.focusOutline}`,
        },
        "&:hover": isInRange
          ? {
              transform: "scale(1.1)",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            }
          : {},
        transition: "transform 0.1s, box-shadow 0.1s",
      }}
    >
      {dayNumber}

      {/* Holiday indicator dot */}
      {holidayName && isInRange && (
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            bottom: 2,
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: COLORS.holidayDot,
            border: "1px solid #fff",
          }}
        />
      )}
    </Box>
  );

  if (holidayName && isInRange) {
    return (
      <Tooltip title={holidayName} arrow>
        {cell}
      </Tooltip>
    );
  }

  return cell;
};

export default DayCell;
